import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../auth';

export default function Sidebar({ onNavigate, isOpen }) {
  const [nodes, setNodes] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);

  const k = (...parts) => parts.join('::');

  // Load root disciplines once per mount
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const disciplinesRes = await fetchWithAuth('/api/disciplines/');
        const disciplines = disciplinesRes.ok ? await disciplinesRes.json() : [];

        if (ignore) return;

        const rootNodes = disciplines.map(d => ({
          type: 'discipline',
          key: k('d', d.id),
          title: d.name,
          disciplineId: d.id,
          enabled: d.is_enabled !== false,
          children: null,
        }));

        setNodes(rootNodes);
      } catch {
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  // Hard reset any time it opens
  useEffect(() => {
    if (!isOpen) return;
    setExpanded(new Set());
    setNodes(prev => prev.map(n => ({ ...n, children: null })));
  }, [isOpen]);

  // helpers
  function updateNode(list, key, updater) {
    return list.map(n =>
      n.key === key ? updater(n) : n.children ? { ...n, children: updateNode(n.children, key, updater) } : n
    );
  }

  async function ensureChildren(node) {
    if (node.children?.length) return;

    // Discipline nodes - load minimal data based on discipline type
    if (node.type === 'discipline') {
      if (!node.enabled) return;
      const disciplineId = node.disciplineId;
      const disciplineName = node.title;

      if (disciplineName.includes('תושב"ע')) {
        try {
          const res = await fetchWithAuth(`/api/disciplines/${disciplineId}/toshba-domains/`);
          const domains = res.ok ? await res.json() : [];
          const kids = [
            // Add all domains first
            ...domains.map(d => ({
              type: 'toshba-domain',
              key: k('d', disciplineId, 'domain', d.route),
              title: d.name,
              domainRoute: d.route,
              domainName: d.name,
              enabled: true,
              children: null,
            })),
            // Then add "How to Teach" link
            {
              type: 'link',
              key: k('d', disciplineId, 'how-to-teach'),
              title: 'הכנה למבחן מפמ"ר',
              href: '/toshba/how-to-teach',
            },
          ];
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        } catch {}
      } else if (disciplineName.includes('מחשבת ישראל')) {
        try {
          const res = await fetchWithAuth(`/api/disciplines/${disciplineId}/jewish-thought-subtopics/`);
          const subtopics = res.ok ? await res.json() : [];
          const kids = subtopics.filter(s => s.is_enabled).map(s => ({
            type: 'jewish-thought-subtopic',
            key: k('d', disciplineId, 'subtopic', s.id),
            title: s.name,
            subtopicId: s.id,
            enabled: s.is_enabled !== false,
            children: null,
          }));
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        } catch {}
      } else if (disciplineName.includes('תנ"ך')) {
        try {
          const res = await fetchWithAuth(`/api/disciplines/${disciplineId}/tanach-books/`);
          const books = res.ok ? await res.json() : [];
          const kids = books.filter(b => b.is_enabled).map(b => ({
            type: 'tanach-book',
            key: k('d', disciplineId, 'book', b.id),
            title: b.name,
            bookId: b.id,
            enabled: b.is_enabled !== false,
            children: null,
          }));
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        } catch {}
      } else if (disciplineName.includes('רבני בתי ספר')) {
        try {
          const res = await fetchWithAuth(`/api/disciplines/${disciplineId}/school-rabbis-main-topics/`);
          const mainTopics = res.ok ? await res.json() : [];
          const kids = mainTopics.filter(t => t.is_enabled).map(t => ({
            type: 'school-rabbis-main-topic',
            key: k('d', disciplineId, 'main', t.id),
            title: t.name,
            mainTopicId: t.id,
            enabled: t.is_enabled !== false,
            children: null,
          }));
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        } catch {}
      }
      return;
    }

    // Toshba domain - load subtopics
    if (node.type === 'toshba-domain') {
      if (!node.enabled) return;
      const domainMap = {
        'משנה': { api: 'subtopic-mishna', route: 'mishna' },
        'גמרא': { api: 'subtopic-gmara', route: 'gmara' },
        'הלכה': { api: 'subtopic-halacha', route: 'halacha' }
      };
      const domainInfo = domainMap[node.domainName] || { api: '', route: '' };
      if (!domainInfo.api) return;

      try {
        const res = await fetchWithAuth(`/api/${domainInfo.api}/?domain_name=${node.domainName}`);
        const subtopics = res.ok ? await res.json() : [];
        
        // Add fixed type links first for gmara, mishna, and halacha
        const kids = [];
        if (domainInfo.route === 'gmara') {
          kids.push(
            {
              type: 'link',
              key: k('d', node.domainRoute, 'foundations'),
              title: 'הישגים בהוראת גמרא',
              href: '/toshba/classes/gmara/foundations',
            },
            {
              type: 'link',
              key: k('d', node.domainRoute, 'structure'),
              title: 'המלצות להוראה מיטבית',
              href: '/toshba/classes/gmara/structure',
            }
          );
        } else if (domainInfo.route === 'mishna') {
          kids.push(
            {
              type: 'link',
              key: k('d', node.domainRoute, 'plan'),
              title: 'הישגים בהוראת משנה',
              href: '/toshba/classes/mishna/plan',
            },
            {
              type: 'link',
              key: k('d', node.domainRoute, 'foundations'),
              title: 'המלצות להוראה מיטבית',
              href: '/toshba/classes/mishna/foundations',
            }
          );
        } else if (domainInfo.route === 'halacha') {
          kids.push(
            {
              type: 'link',
              key: k('d', node.domainRoute, 'plan'),
              title: 'הישגים בהוראת הלכה',
              href: '/toshba/classes/halacha/plan',
            },
            {
              type: 'link',
              key: k('d', node.domainRoute, 'skills'),
              title: 'המלצות להוראה מיטבית',
              href: '/toshba/classes/halacha/skills',
            }
          );
        }
        
        // Then add all subtopics
        const subtopicLinks = subtopics.filter(s => s.is_enabled).map(s => {
          const subtopicId = s.id;
          return {
            type: 'link',
            key: k('d', node.domainRoute, 'sub', subtopicId),
            title: s.name,
            href: `/toshba/classes/${node.domainRoute}/${subtopicId}`,
          };
        });
        kids.push(...subtopicLinks);
        
        setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
      } catch {}
      return;
    }

    // Tanach book - show direct links to chapters and general topics
    if (node.type === 'tanach-book') {
      if (!node.enabled) return;
      const kids = [
        {
          type: 'link',
          key: k('d', node.bookId, 'chapters'),
          title: 'פרקים',
          href: `/tanach/books/${node.bookId}/chapters`,
        },
        {
          type: 'link',
          key: k('d', node.bookId, 'general'),
          title: 'נושאים כלליים',
          href: `/tanach/books/${node.bookId}/general-topics`,
        }
      ];
      setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
      return;
    }

    // Tanach book chapters list - load chapters
    if (node.type === 'tanach-book-chapters') {
      if (!node.enabled) return;
      try {
        const res = await fetchWithAuth(`/api/tanach-books/${node.bookId}/`);
        const book = res.ok ? await res.json() : null;
        if (book && book.chapters) {
          const kids = book.chapters.filter(c => c.is_enabled).map(c => ({
            type: 'tanach-chapter',
            key: k('d', node.bookId, 'ch', c.id),
            title: c.name,
            bookId: node.bookId,
            chapterId: c.id,
            enabled: c.is_enabled !== false,
            children: null,
          }));
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        }
      } catch {}
      return;
    }

    // Tanach chapter - load topics
    if (node.type === 'tanach-chapter') {
      if (!node.enabled) return;
      try {
        const res = await fetchWithAuth(`/api/tanach-book-chapters/${node.chapterId}/`);
        const chapter = res.ok ? await res.json() : null;
        if (chapter && chapter.topics) {
          const kids = chapter.topics.filter(t => t.is_enabled).map(t => ({
            type: 'link',
            key: k('d', node.bookId, 'ch', node.chapterId, 'topic', t.id),
            title: t.name,
            href: `/tanach/books/${node.bookId}/chapters/${node.chapterId}/topics/${t.id}`,
          }));
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        }
      } catch {}
      return;
    }

    // School rabbis main topic - load secondary topics
    if (node.type === 'school-rabbis-main-topic') {
      if (!node.enabled) return;
      try {
        const res = await fetchWithAuth(`/api/school-rabbis-main-topics/${node.mainTopicId}/`);
        const mainTopic = res.ok ? await res.json() : null;
        if (mainTopic && mainTopic.secondary_topics) {
          const kids = mainTopic.secondary_topics.filter(t => t.is_enabled).map(t => ({
            type: 'school-rabbis-secondary-topic',
            key: k('d', node.mainTopicId, 'sec', t.id),
            title: t.name,
            mainTopicId: node.mainTopicId,
            secondaryTopicId: t.id,
            enabled: t.is_enabled !== false,
            children: null,
          }));
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        }
      } catch {}
      return;
    }

    // School rabbis secondary topic - load projects
    if (node.type === 'school-rabbis-secondary-topic') {
      if (!node.enabled) return;
      try {
        const res = await fetchWithAuth(`/api/school-rabbis-secondary-topics/${node.secondaryTopicId}/`);
        const secondaryTopic = res.ok ? await res.json() : null;
        if (secondaryTopic && secondaryTopic.projects) {
          const kids = secondaryTopic.projects.filter(p => p.is_enabled).map(p => ({
            type: 'link',
            key: k('d', node.mainTopicId, 'sec', node.secondaryTopicId, 'proj', p.id),
            title: p.name,
            href: `/school-rabbis/main-topics/${node.mainTopicId}/secondary-topics/${node.secondaryTopicId}/projects/${p.id}`,
          }));
          setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
        }
      } catch {}
      return;
    }

    // Jewish thought subtopic - load sources
    if (node.type === 'jewish-thought-subtopic') {
      if (!node.enabled) return;
      try {
        const res = await fetchWithAuth(`/api/jewish-thought-subtopics/${node.subtopicId}/sources/`);
        const sources = res.ok ? await res.json() : [];
        const kids = [
          // Add main subtopic link first
          {
            type: 'link',
            key: k('d', node.subtopicId, 'main'),
            title: 'עמוד ראשי',
            href: `/jewish-thought/subtopics/${node.subtopicId}`,
          },
          // Then add all sources
          ...sources.filter(s => s.is_enabled).map(s => ({
            type: 'link',
            key: k('d', node.subtopicId, 'source', s.id),
            title: s.name,
            href: `/jewish-thought/subtopics/${node.subtopicId}/sources/${s.id}`,
          }))
        ];
        setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
      } catch {}
      return;
    }
  }

  function onToggle(node, open) {
    if ((node.type === 'discipline' || node.type === 'toshba-domain' || node.type === 'tanach-book' ||
         node.type === 'tanach-chapter' || node.type === 'school-rabbis-main-topic' ||
         node.type === 'school-rabbis-secondary-topic' || node.type === 'jewish-thought-subtopic') && !node.enabled) return;
    setExpanded(prev => {
      const next = new Set(prev);
      open ? next.add(node.key) : next.delete(node.key);
      return next;
    });
    if (open) ensureChildren(node);
  }

  const hasExpanded = expanded.size > 0;

  return (
    <nav className="h-full flex flex-col" role="navigation" aria-label="ניווט">
      {/* Sidebar header */}
      <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
        <span className="font-semibold text-secondary-900 dark:text-white">ניווט</span>
        <button
          type="button"
          className={`
            px-3 py-1 text-sm rounded-lg
            text-secondary-600 dark:text-secondary-300
            hover:bg-secondary-100 dark:hover:bg-secondary-800
            transition-all duration-200
            ${hasExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          onClick={() => setExpanded(new Set())}
          aria-label="כווץ"
          title="כווץ"
        >
          כווץ
        </button>
      </div>

      {/* Navigation tree */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-6 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <Tree nodes={nodes} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />
        )}
      </div>
    </nav>
  );
}

function Tree({ nodes, expanded, onToggle, onNavigate }) {
  return (
    <div className="space-y-1">
      {nodes.map(n => (
        <TreeNode key={n.key} node={n} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function TreeNode({ node, expanded, onToggle, onNavigate }) {
  const isOpen = expanded.has(node.key);
  const isLink = node.type === 'link';

  // Disabled node
  if ((node.type === 'discipline' || node.type === 'toshba-domain' || node.type === 'tanach-book' ||
       node.type === 'tanach-chapter' || node.type === 'school-rabbis-main-topic' ||
       node.type === 'school-rabbis-secondary-topic' || node.type === 'jewish-thought-subtopic') && !node.enabled) {
    return (
      <div className="py-1.5 px-2 text-secondary-400 dark:text-secondary-500 cursor-not-allowed">
        <span>{node.title}</span>
        <small className="ms-1 text-xs opacity-70">(בקרוב)</small>
      </div>
    );
  }

  // Link node
  if (isLink) {
    return (
      <Link
        to={node.href}
        state={node.state}
        onClick={onNavigate}
        className="
          block py-1.5 px-2 rounded-lg
          text-secondary-700 dark:text-secondary-200
          hover:bg-primary-50 hover:text-primary-600
          dark:hover:bg-primary-900/20 dark:hover:text-primary-400
          transition-colors duration-150
        "
      >
        {node.title}
      </Link>
    );
  }

  // Expandable node
  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(node, !isOpen)}
        className="
          w-full flex items-center gap-2 py-1.5 px-2 rounded-lg
          text-secondary-700 dark:text-secondary-200
          hover:bg-secondary-100 dark:hover:bg-secondary-800
          transition-colors duration-150
          text-start
        "
        aria-expanded={isOpen}
      >
        <svg
          className={`w-4 h-4 text-secondary-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="font-medium">{node.title}</span>
      </button>

      {isOpen && node.children?.length > 0 && (
        <div className="ms-4 ps-3 border-s border-secondary-200 dark:border-secondary-700 mt-1">
          <Tree nodes={node.children} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />
        </div>
      )}
    </div>
  );
}
